import Signal from '../../system/signals/Signal' ;

import config from '../configs/config' ;
import roles  from '../roles'
import Roles from "../net/auth/Roles";

import Person from './Person' ;

const ID = config.name + '_user' ;

/**
 * Defines the user of the application to save in the session storage of the application.
 * @memberOf things
 */
class User extends Person
{
    /**
     * Creates a new User instance.
     * @constructor
     * @param object The generic object to initialize the object the first time.
     */
    constructor( object = null )
    {
        super() ;

        this.change = new Signal() ;

        /**
         * The provider of the user.
         */
        this.provider = null ;

        /**
         * The provider user identifier value.
         */
        this.provider_uid = null ;

        /**
         * The scope settings of the user.
         */
        this.scope = null ;

        /**
         * The team of the user.
         */
        this.team = null ;

        /**
         * The unique user identifier of the user.
         */
        this.uuid = null ;

        if( object )
        {
            for (let prop in object)
            {
                this[prop] = object[prop];
            }
            this.populate() ;
        }
    }

    /**
     * The session storage ID of the User singleton register during the authentication process.
     * @type {string}
     */
    static ID = ID ;

    /**
     * Indicates if the object is stored in the session storage context.
     * @returns {boolean} True if the object is stored.
     */
    get stored()
    {
        return (sessionStorage.getItem( ID ) !== null) ;
    }

    /**
     * Clear the object.
     */
    clear()
    {
        super.clear() ;

        this.provider      = null ;
        this.provider_uid  = null ;
        this.scope         = null ;
        this.team          = null ;
        this.uuid          = null ;

        this.change.emit() ;

        return this ;
    }

    /**
     * Returns a shallow copy of the object.
     * @return {Thing} a shallow copy of the object.
     */
    clone()
    {
        return new User( this.toObject() ) ;
    }

    /**
     * Check if the passed-in path is a valid.
     * @param path The path to check
     * @returns {boolean} Indicates if the passed-in path is valid.
     */
    checkScopeByPath( path )
    {
        if( path.indexOf('/') === 0 )
        {
            path = path.slice(1) ;
        }
        path = path.split('/')[0] ;

        let current = (this.scope && this.scope.hasOwnProperty(path)) ? this.scope[path] : null ;
        let scopes  = roles.hasOwnProperty(path) ? roles[path] : null ;

        if( !current )
        {
            return scopes == null ;
        }

        return scopes.indexOf(current) > -1 ;
    }

    /**
     * Unregister the object in the local storage.
     */
    dispose()
    {
        if( sessionStorage.getItem(ID) !== null )
        {
            sessionStorage.removeItem(ID) ;
        }
        return this ;
    }

    /**
     * Returns the current scope of the user with the specific path.
     * @param path The path to check
     * @returns {string} the current scope of the user with the specific path.
     */
    getScopeByPath( path )
    {
        if( path.indexOf('/') === 0 )
        {
            path = path.slice(1) ;
        }
        path = path.split('/')[0] ;
        return (this.scope && this.scope.hasOwnProperty(path)) ? this.scope[path] : null ;
    }

    getFullName()
    {
        if( !this.givenName || !this.familyName )
        {
            return null ;
        }

        return this.givenName + " " + this.familyName ;
    }

    getFullNameOrUsername()
    {
        let name = [] ;

        if( this.givenName )
        {
            name.push( this.givenName ) ;
        }

        if( this.familyName )
        {
            name.push( this.familyName ) ;
        }

        if( (name.length === 0) && this.name )
        {
            name.push( this.name ) ;
        }

        return name.join(' ') ;
    }

    isAdmin( path )
    {
        return this.getScopeByPath( path ) === Roles.ADMIN ;
    }

    isGuest( path )
    {
        return this.getScopeByPath( path ) === Roles.GUEST ;
    }

    isRead( path )
    {
        return this.getScopeByPath( path ) === Roles.READ ;
    }

    isWrite( path )
    {
        return this.getScopeByPath( path ) === Roles.WRITE ;
    }

    /**
     * Initialize the object with the current session storage.
     */
    read()
    {
        let object = JSON.parse( sessionStorage.getItem( ID ) ) ;
        if( object != null )
        {
            for( let prop in object )
            {
                if( object.hasOwnProperty(prop) && this.hasOwnProperty(prop) )
                {
                    this[prop] = object[prop] ;
                }
            }
        }
        return this ;
    }

    /**
     * Sets the object with a generic object and write it on the server.
     */
    set( object )
    {
        if( object )
        {
            for (let prop in object)
            {
                this[prop] = object[prop];
            }
            this.change.emit();
        }
        return this ;
    }

    /**
     * Returns the generic object representation of the object.
     * @returns {{access_token: *, expire_in: number, refresh_token: *, timestamp: *, token_type: *, scope: *, state: *}}
     */
    toObject()
    {
        return {
            ...super.toObject() ,
            ...{
                provider: this.provider,
                provider_uid: this.provider_uid,
                scope: this.scope,
                team: this.team,
                uuid: this.uuid
        }};
    }

    /**
     * Write the object in the session storage with the specific id.
     */
    write()
    {
        sessionStorage.setItem( ID , JSON.stringify(this.toObject()) ) ;
        return this ;
    }
}

export default User ;


// WEBPACK FOOTER //
// ./src/app/things/User.js